import { select } from "@angular-redux/store";
import { Component, forwardRef, Inject, ViewChild } from '@angular/core';
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { LicensePackageOptions, LicensePackageType } from 'sharedFeatures/enums';
import { ComboComponent, ItemData } from "sharedFeatures/form";
import { Account, AuthData, paymentHistory, PaymentHistoryYears, SpareLicenseModel } from "sharedFeatures/sharedModels";
import { BillingService, DialogCreationService } from "sharedFeatures/sharedServices";
import { AuthUtils, Utils } from 'sharedFeatures/utils';
import { BillingPaymentHistoryPaneComponent } from './billing-payment-history-pane.component';
import { IPaymentHistoryResponse,
    ICustomerLicensesResponse,
    IErrorResponse,
    isErrorResponse,
    isSuccessResponse,
} from "sharedFeatures/sharedServices/Responses/billing.index";

declare var jQuery: any;
@Component(
    {
        selector: 'billing-payment-history-body',
        templateUrl: './billing-payment-history-body.component.html',
        styleUrls: ['./billing-payment-history-body.component.less']
    })

export class BillingPaymentHistoryBodyComponent {

    errorText = $localize`Error`;
    allSubscriptionsText = $localize`All subscriptions`;
    getInvoiceForOrderText = $localize`Get invoice for order`;
    failedText = $localize`failed`;
    downloadDocumentText = $localize`DOWNLOAD_DOCUMENT`;

    @ViewChild(ComboComponent)
    private subscriptionCombo!: ComboComponent;

    // Unsubscribe event
    private storeUnsubscribe = new Subject<void>();

    @select(['account', 'authData'])
    readonly authData$!: Observable<AuthData>;
    authData: AuthData = new AuthData();

    @select(['account', 'accountData'])
    readonly accountData$!: Observable<Account>;
    private accountData: Account = new Account();

    years: Array<number> = [];
    minYear: number = 0;
    maxYear: number = 0 ;
    currentIndex: number = 0;
    currentYear: number = 0;

    leftEnabled: boolean = false;
    rightEnabled: boolean = false;
    orders: Array<paymentHistory> = [];
    subscribtions: Array<ItemData> = [{ id: 0, value: this.allSubscriptionsText, disabled: false }];
    subscribtionRef: string = this.allSubscriptionsText;
    subscriptionId: number = 0;

    licenses: Array<SpareLicenseModel> = [];

    constructor(private billingService: BillingService,
        @Inject(forwardRef(() => BillingPaymentHistoryPaneComponent)) private parent: BillingPaymentHistoryPaneComponent,
        private dialogCreation: DialogCreationService) {

        //this.UserSubscription = new Subscription();
        this.LoadPaymentHistoryYears();
    }

    ngOnInit() {
        // Subscribe on the redux store
        this.subscribeReduxStore();
        this.loadCustomerLicenses();
    }

    ngAfterViewInit() {
        this.subscriptionCombo.additionalListStyles.push(['font-family', 'Source Sans Pro, Arial, sans-serif'], ['font-size', '16px']);
    }

    ngOnDestroy() {
        // Unsubscribe from the redux store
        this.storeUnsubscribe.next();
        this.storeUnsubscribe.complete();
    }

    // Subscribe on the redux store
    private subscribeReduxStore(): void {

        this.accountData$.pipe(
            takeUntil(this.storeUnsubscribe))
            .subscribe(x => this.accountData = x);

        this.authData$.pipe(
            takeUntil(this.storeUnsubscribe))
            .subscribe(x => {
                this.authData = x;
            });
    }

    enableArrows(): void {
        this.leftEnabled = false;
        this.rightEnabled = false;

        if (this.years.length <= 1)
            return;

        if (this.currentYear > this.minYear)
            this.leftEnabled = true;

        if (this.currentYear < this.maxYear)
            this.rightEnabled = true;
    }

    scrollLeft(): void {
        if (this.currentIndex == 0)
            return;

        this.currentIndex--;
        this.currentYear = this.years[this.currentIndex];
        this.LoadPaymentHistory();
    }

    scrollRight(): void {

        if (this.currentIndex == this.years.length)
            return;

        this.currentIndex++;
        this.currentYear = this.years[this.currentIndex];
        this.LoadPaymentHistory();
    }

    LoadPaymentHistory(callback?: Function): void {
        this.billingService.LoadPaymentHistory(this.currentYear.toString(), this.subscribtionRef)
            .then((data: IPaymentHistoryResponse | undefined) => {
                if (data === undefined || isErrorResponse(data)) return;

                this.orders = (<IPaymentHistoryResponse>data).history;

                this.enableArrows();
                if (callback)
                    callback();
            })
            .catch(error => {
                this.dialogCreation.createUnknownExceptionDialog(error).show();
                this.enableArrows();
            });
    }

    LoadPaymentHistoryYears(): void {
        this.billingService.LoadPaymentHistoryYears().then((data: PaymentHistoryYears) => {
            if (data.success) {
                this.years = data.years;

                if (this.years.length > 0) {
                    this.currentIndex = data.currentIndex;
                    this.currentYear = this.years[this.currentIndex];
                    this.minYear = data.minYear;
                    this.maxYear = data.maxYear;
                }

                this.LoadPaymentHistory();
            }
        }, (error) => {
            this.dialogCreation.createUnknownExceptionDialog(error).show();
        });

    }

    DownloadInvoice(OrderRef: string): void {

        jQuery.fileDownload(
            `${window.appPath}Subscription/GetInvoiceDocument`,
            {
                data: {
                    customerRef: this.accountData.customerRef.toString(),
                    orderReference: OrderRef
                },
                httpMethod: 'POST',
                failCallback: (responseHtml: string, url: string) => {
                    this.dialogCreation.createUnknownExceptionDialog(`${this.getInvoiceForOrderText} ${OrderRef} ${this.failedText}`).show();
                }
            });
    }

    onSubscriptionChanged(ev: number): void {
        if (ev == 0)
            this.subscribtionRef = this.allSubscriptionsText;

        let filteredSubscription = this.licenses.filter(x => x.ID == ev);
        if (filteredSubscription.length > 0) {
            this.subscribtionRef = filteredSubscription[0].SubscriptionReference;
        }

        if (this.currentYear != 0)
            this.LoadPaymentHistory();
    }

    private getLicenseName(x: SpareLicenseModel, showAvailable: boolean): string {

        let packageCount = '';

        let packageType = AuthUtils.getLicensePackageType([x.PriceOption1, x.PriceOption2]);

        return LicensePackageOptions.get(packageType)!.OptionName + packageCount + ' - ID ' + x.SubscriptionReference;
    }

    loadCustomerLicenses(): void {

        this.billingService.GetCustomerLicenses()
            .then((data: ICustomerLicensesResponse | IErrorResponse) => {
                if (isSuccessResponse(data)) {
                    data = data as ICustomerLicensesResponse;
                    this.licenses = data.customerLicenses;
                    this.licenses.forEach(x => {
                        let item = new ItemData();
                        item.value = this.getLicenseName(x, true);
                        item.id = x.ID;
                        this.subscribtions.push(item);
                    });
                }
                else {
                    data = data as IErrorResponse;
                    const popup = this.dialogCreation.createErrorWarning(this.errorText, data.errorMessage, () => {
                        popup.close();
                    });
                    popup.show();

                }

            })
            .catch(error => {
                this.dialogCreation.createUnknownExceptionDialog(error).show();
            });
    }
}
